import { useState } from 'react';

import { ApiError, api } from '../../api/client';
import type { Candidate, Requirement, RequirementItem, RolePriority } from '../../api/types';
import { Banner, Card, Empty, PriorityBadge, formatDate, secondaryLabel } from '../../components/ui';
import { useAsync } from '../../hooks/useAsync';
import { useAuth } from '../../hooks/useAuth';

type Guard = (action: () => Promise<unknown>) => Promise<void>;

interface Role {
  id: number;
  name: string;
}

/**
 * Événements et gabarits de besoins (brief §9 et §10).
 *
 * Deux onglets, parce que ce sont deux objets différents pour une même
 * question : un gabarit décrit les besoins d'un **type** de rencontre, un
 * événement ceux d'une date précise. Les besoins d'un événement sont une
 * **copie** du gabarit, jamais une référence — c'est ce qui permet au §9
 * d'avoir « basse optionnelle le dimanche, obligatoire en soirée spéciale ».
 */
export default function EventsScreen() {
  const { isAdmin } = useAuth();
  const [tab, setTab] = useState<'dates' | 'gabarits'>('dates');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const roles = useAsync(() => api.roles(), []);

  const guard: Guard = async (action) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await action();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.detail : 'Action impossible.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <h1>Événements et besoins</h1>
      <div className="choice-group" style={{ maxWidth: 420, marginBottom: 16 }}>
        <button
          className="choice"
          aria-pressed={tab === 'dates'}
          onClick={() => setTab('dates')}
        >
          Dates
        </button>
        <button
          className="choice"
          aria-pressed={tab === 'gabarits'}
          onClick={() => setTab('gabarits')}
        >
          Gabarits de besoins
        </button>
      </div>

      {error && <Banner kind="error">{error}</Banner>}
      {notice && <Banner kind="success">{notice}</Banner>}

      {tab === 'dates' ? (
        <DatesPanel
          roles={roles.data ?? []}
          busy={busy}
          guard={guard}
          onNotice={setNotice}
        />
      ) : (
        <TemplatesPanel
          roles={roles.data ?? []}
          busy={busy}
          guard={guard}
          canEdit={isAdmin}
          onNotice={setNotice}
        />
      )}
    </>
  );
}

// --------------------------------------------------------------------------- //
// Dates
// --------------------------------------------------------------------------- //

function DatesPanel({
  roles,
  busy,
  guard,
  onNotice,
}: {
  roles: Role[];
  busy: boolean;
  guard: Guard;
  onNotice: (message: string) => void;
}) {
  const [selected, setSelected] = useState<number | null>(null);

  const events = useAsync(() => api.events(), []);
  const eventTypes = useAsync(() => api.eventTypes(), []);
  const detail = useAsync(
    () => (selected === null ? Promise.resolve(null) : api.event(selected)),
    [selected],
  );
  const candidates = useAsync<Candidate[]>(
    () => (selected === null ? Promise.resolve([]) : api.candidates(selected)),
    [selected],
  );

  const [title, setTitle] = useState('');
  const [day, setDay] = useState('');
  const [typeId, setTypeId] = useState('');

  const createEvent = async (submit: React.FormEvent) => {
    submit.preventDefault();
    await guard(async () => {
      const created = await api.createEvent({
        title,
        starts_at: `${day}T10:00:00`,
        event_type_id: typeId === '' ? null : Number(typeId),
      });
      setTitle('');
      setDay('');
      events.reload();
      setSelected(created.id);
    });
  };

  return (
    <div className="two-columns">
      <Card title="Dates">
        <form className="stack" onSubmit={createEvent} style={{ marginBottom: 16 }}>
          <div>
            <label htmlFor="title">Intitulé</label>
            <input
              id="title"
              required
              style={{ width: '100%' }}
              value={title}
              onChange={(event) => setTitle(event.target.value)}
            />
          </div>
          <div>
            <label htmlFor="date">Date</label>
            <input
              id="date"
              type="date"
              required
              style={{ width: '100%' }}
              value={day}
              onChange={(event) => setDay(event.target.value)}
            />
          </div>
          <div>
            <label htmlFor="type">Gabarit de besoins</label>
            <select
              id="type"
              style={{ width: '100%' }}
              value={typeId}
              onChange={(event) => setTypeId(event.target.value)}
            >
              <option value="">Aucun</option>
              {(eventTypes.data ?? []).map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </div>
          <button className="primary" type="submit" disabled={busy}>
            Ajouter la date
          </button>
        </form>

        {events.loading ? (
          <p className="muted">Chargement…</p>
        ) : (events.data ?? []).length === 0 ? (
          <Empty>Aucune date enregistrée.</Empty>
        ) : (
          <div className="stack">
            {(events.data ?? []).map((event) => (
              <button
                key={event.id}
                className={selected === event.id ? 'primary' : 'ghost'}
                style={{ textAlign: 'left' }}
                onClick={() => setSelected(event.id)}
              >
                {formatDate(event.starts_at)}
                <br />
                <span className="muted">{secondaryLabel(event.title, event.starts_at)}</span>
              </button>
            ))}
          </div>
        )}
      </Card>

      <div>
        {detail.data === null ? (
          <Card>
            <Empty>Choisissez une date pour la modifier et régler ses besoins.</Empty>
          </Card>
        ) : (
          <>
            <EventForm
              key={detail.data.id}
              event={detail.data}
              busy={busy}
              onSave={(changes) =>
                guard(async () => {
                  await api.updateEvent(detail.data!.id, changes);
                  detail.reload();
                  events.reload();
                  onNotice('Événement enregistré.');
                })
              }
              onDelete={() =>
                guard(async () => {
                  await api.deleteEvent(detail.data!.id);
                  setSelected(null);
                  events.reload();
                  onNotice('Événement supprimé.');
                })
              }
            />
            <Card title="Besoins">
              <RequirementsEditor
                roles={roles}
                requirements={detail.data.requirements ?? []}
                busy={busy}
                candidates={candidates.data ?? []}
                onReplace={(next) =>
                  guard(async () => {
                    await api.replaceRequirements(detail.data!.id, next);
                    detail.reload();
                    candidates.reload();
                  })
                }
              />
              <BlockingList candidates={candidates.data ?? []} roles={roles} />
            </Card>
          </>
        )}
      </div>
    </div>
  );
}

function EventForm({
  event,
  busy,
  onSave,
  onDelete,
}: {
  event: {
    id: number;
    title: string;
    starts_at: string;
    location: string | null;
    is_cancelled: boolean;
  };
  busy: boolean;
  onSave: (changes: Record<string, unknown>) => Promise<void>;
  onDelete: () => Promise<void>;
}) {
  const [draft, setDraft] = useState({
    title: event.title,
    // `datetime-local` attend « AAAA-MM-JJTHH:MM », sans fuseau ni secondes.
    starts_at: event.starts_at.slice(0, 16),
    location: event.location ?? '',
    is_cancelled: event.is_cancelled,
  });
  const [confirming, setConfirming] = useState(false);
  const dateChanged = draft.starts_at !== event.starts_at.slice(0, 16);

  return (
    <Card title="Modifier la date">
      <form
        className="stack"
        onSubmit={(submit) => {
          submit.preventDefault();
          void onSave({
            title: draft.title,
            starts_at: `${draft.starts_at}:00`,
            location: draft.location || null,
            is_cancelled: draft.is_cancelled,
          });
        }}
      >
        <div className="row">
          <div style={{ flex: 2 }}>
            <label htmlFor="edit-title">Intitulé</label>
            <input
              id="edit-title"
              required
              style={{ width: '100%' }}
              value={draft.title}
              onChange={(change) =>
                setDraft((d) => ({ ...d, title: change.target.value }))
              }
            />
          </div>
          <div>
            <label htmlFor="edit-start">Date et heure</label>
            <input
              id="edit-start"
              type="datetime-local"
              required
              value={draft.starts_at}
              onChange={(change) =>
                setDraft((d) => ({ ...d, starts_at: change.target.value }))
              }
            />
          </div>
        </div>
        <div>
          <label htmlFor="edit-location">Lieu</label>
          <input
            id="edit-location"
            style={{ width: '100%' }}
            value={draft.location}
            onChange={(change) =>
              setDraft((d) => ({ ...d, location: change.target.value }))
            }
          />
        </div>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            type="checkbox"
            checked={draft.is_cancelled}
            onChange={(change) =>
              setDraft((d) => ({ ...d, is_cancelled: change.target.checked }))
            }
          />
          Événement annulé (il disparaît des prochaines générations)
        </label>

        {dateChanged && (
          <Banner kind="info">
            Vous changez la date. Les disponibilités déjà saisies ne sont pas effacées,
            mais elles ont été données pour l'ancienne date : pensez à prévenir les
            personnes concernées.
          </Banner>
        )}

        <div className="spread">
          <button className="primary" type="submit" disabled={busy}>
            Enregistrer
          </button>
          {confirming ? (
            <span className="row" style={{ alignItems: 'center' }}>
              <span className="muted">Supprimer définitivement ?</span>
              <button
                type="button"
                className="small"
                disabled={busy}
                onClick={() => {
                  setConfirming(false);
                  void onDelete();
                }}
              >
                Oui, supprimer
              </button>
              <button
                type="button"
                className="ghost small"
                onClick={() => setConfirming(false)}
              >
                Annuler
              </button>
            </span>
          ) : (
            <button
              type="button"
              className="ghost small"
              onClick={() => setConfirming(true)}
            >
              Supprimer
            </button>
          )}
        </div>
        <p className="muted">
          Un événement qui figure dans un planning publié ne peut pas être supprimé :
          annulez-le plutôt, l'historique reste alors intact.
        </p>
      </form>
    </Card>
  );
}

// --------------------------------------------------------------------------- //
// Gabarits
// --------------------------------------------------------------------------- //

function TemplatesPanel({
  roles,
  busy,
  guard,
  canEdit,
  onNotice,
}: {
  roles: Role[];
  busy: boolean;
  guard: Guard;
  canEdit: boolean;
  onNotice: (message: string) => void;
}) {
  const [selected, setSelected] = useState<number | null>(null);
  const [name, setName] = useState('');

  const templates = useAsync(() => api.eventTypes(), []);
  const requirements = useAsync(
    () => (selected === null ? Promise.resolve([]) : api.eventTypeRequirements(selected)),
    [selected],
  );
  const current = (templates.data ?? []).find((item) => item.id === selected) ?? null;

  if (!canEdit) {
    return (
      <Banner kind="info">
        Les gabarits sont gérés par un administrateur. Vous pouvez les utiliser à la
        création d'une date.
      </Banner>
    );
  }

  return (
    <div className="two-columns">
      <Card title="Gabarits">
        <form
          className="stack"
          style={{ marginBottom: 16 }}
          onSubmit={(submit) => {
            submit.preventDefault();
            void guard(async () => {
              const created = await api.createEventType({ name });
              setName('');
              templates.reload();
              setSelected(created.id);
            });
          }}
        >
          <div>
            <label htmlFor="type-name">Nouveau gabarit</label>
            <input
              id="type-name"
              required
              placeholder="Soirée de louange"
              style={{ width: '100%' }}
              value={name}
              onChange={(change) => setName(change.target.value)}
            />
          </div>
          <button className="primary" type="submit" disabled={busy}>
            Créer
          </button>
        </form>

        {(templates.data ?? []).length === 0 ? (
          <Empty>Aucun gabarit.</Empty>
        ) : (
          <div className="stack">
            {(templates.data ?? []).map((item) => (
              <button
                key={item.id}
                className={selected === item.id ? 'primary' : 'ghost'}
                style={{ textAlign: 'left' }}
                onClick={() => setSelected(item.id)}
              >
                {item.name}
              </button>
            ))}
          </div>
        )}
        <p className="muted" style={{ marginTop: 12 }}>
          Un gabarit sert de point de départ : ses besoins sont <strong>copiés</strong> à
          la création d'une date, puis modifiables indépendamment.
        </p>
      </Card>

      <div>
        {current === null ? (
          <Card>
            <Empty>Choisissez un gabarit.</Empty>
          </Card>
        ) : (
          <>
            <TemplateForm
              key={current.id}
              template={current}
              busy={busy}
              onSave={(changes) =>
                guard(async () => {
                  await api.updateEventType(current.id, changes);
                  templates.reload();
                  onNotice('Gabarit enregistré.');
                })
              }
              onDelete={() =>
                guard(async () => {
                  await api.deleteEventType(current.id);
                  setSelected(null);
                  templates.reload();
                  onNotice('Gabarit supprimé. Les dates existantes gardent leurs besoins.');
                })
              }
            />
            <Card title="Besoins du gabarit">
              <RequirementsEditor
                roles={roles}
                requirements={requirements.data ?? []}
                busy={busy}
                candidates={[]}
                onReplace={(next) =>
                  guard(async () => {
                    await api.replaceEventTypeRequirements(current.id, next);
                    requirements.reload();
                  })
                }
              />
            </Card>
          </>
        )}
      </div>
    </div>
  );
}

function TemplateForm({
  template,
  busy,
  onSave,
  onDelete,
}: {
  template: { id: number; name: string; description: string | null };
  busy: boolean;
  onSave: (changes: Record<string, unknown>) => Promise<void>;
  onDelete: () => Promise<void>;
}) {
  const [draft, setDraft] = useState({
    name: template.name,
    description: template.description ?? '',
  });
  const [confirming, setConfirming] = useState(false);

  return (
    <Card title="Modifier le gabarit">
      <form
        className="stack"
        onSubmit={(submit) => {
          submit.preventDefault();
          void onSave({ name: draft.name, description: draft.description || null });
        }}
      >
        <div>
          <label htmlFor="type-edit-name">Nom</label>
          <input
            id="type-edit-name"
            required
            style={{ width: '100%' }}
            value={draft.name}
            onChange={(change) => setDraft((d) => ({ ...d, name: change.target.value }))}
          />
        </div>
        <div>
          <label htmlFor="type-edit-desc">Description</label>
          <input
            id="type-edit-desc"
            style={{ width: '100%' }}
            value={draft.description}
            onChange={(change) =>
              setDraft((d) => ({ ...d, description: change.target.value }))
            }
          />
        </div>
        <div className="spread">
          <button className="primary" type="submit" disabled={busy}>
            Enregistrer
          </button>
          {confirming ? (
            <span className="row" style={{ alignItems: 'center' }}>
              <span className="muted">Supprimer ce gabarit ?</span>
              <button
                type="button"
                className="small"
                disabled={busy}
                onClick={() => {
                  setConfirming(false);
                  void onDelete();
                }}
              >
                Oui
              </button>
              <button
                type="button"
                className="ghost small"
                onClick={() => setConfirming(false)}
              >
                Annuler
              </button>
            </span>
          ) : (
            <button
              type="button"
              className="ghost small"
              onClick={() => setConfirming(true)}
            >
              Supprimer
            </button>
          )}
        </div>
      </form>
    </Card>
  );
}

// --------------------------------------------------------------------------- //
// Besoins, partagés entre une date et un gabarit
// --------------------------------------------------------------------------- //

function RequirementsEditor({
  roles,
  requirements,
  busy,
  candidates,
  onReplace,
}: {
  roles: Role[];
  requirements: Requirement[];
  busy: boolean;
  candidates: Candidate[];
  onReplace: (next: RequirementItem[]) => Promise<void>;
}) {
  const asItems = (): RequirementItem[] =>
    requirements.map((item) => ({
      role_id: item.role_id,
      minimum: item.minimum,
      target: item.target,
      maximum: item.maximum,
      priority: item.priority,
      notes: item.notes ?? null,
    }));

  const update = (roleId: number, change: Partial<RequirementItem>) => {
    const next = asItems();
    const index = next.findIndex((item) => item.role_id === roleId);
    const base: RequirementItem =
      index >= 0
        ? next[index]!
        : {
            role_id: roleId,
            minimum: 0,
            target: 1,
            maximum: 1,
            priority: 'PREFERRED',
            notes: null,
          };
    const merged: RequirementItem = { ...base, ...change };

    // L'interface traduit l'intention humaine vers les invariants du modèle :
    // « obligatoire » implique un minimum d'au moins 1, « souhaité » et
    // « optionnel » un minimum nul (ambiguïté A2). Le responsable n'a pas à
    // connaître cette règle.
    if (merged.priority === 'REQUIRED') {
      merged.minimum = Math.max(1, merged.minimum || 1);
    } else {
      merged.minimum = 0;
    }
    merged.target = Math.max(merged.minimum, merged.target);
    merged.maximum = Math.max(merged.target, merged.maximum);

    if (index >= 0) next[index] = merged;
    else next.push(merged);
    return onReplace(next);
  };

  const remove = (roleId: number) =>
    onReplace(asItems().filter((item) => item.role_id !== roleId));

  const candidateFor = (roleId: number) =>
    candidates.find((row) => row.role_id === roleId) ?? null;

  return (
    <div className="table-scroll">
      <table>
        <thead>
          <tr>
            <th>Rôle</th>
            <th>Importance</th>
            <th>Idéalement</th>
            <th>Au plus</th>
            {candidates.length > 0 && <th>Disponibles</th>}
            <th />
          </tr>
        </thead>
        <tbody>
          {roles.map((role) => {
            const requirement = requirements.find((item) => item.role_id === role.id);
            const candidate = candidateFor(role.id);
            return (
              <tr key={role.id}>
                <td>{role.name}</td>
                <td>
                  <select
                    aria-label={`Importance de ${role.name}`}
                    value={requirement?.priority ?? ''}
                    disabled={busy}
                    onChange={(change) =>
                      change.target.value === ''
                        ? remove(role.id)
                        : update(role.id, {
                            priority: change.target.value as RolePriority,
                          })
                    }
                  >
                    <option value="">Pas de besoin</option>
                    <option value="REQUIRED">Obligatoire</option>
                    <option value="PREFERRED">Souhaité</option>
                    <option value="OPTIONAL">Optionnel</option>
                  </select>
                </td>
                <td>
                  {requirement && (
                    <input
                      type="number"
                      min={0}
                      max={20}
                      style={{ width: 70 }}
                      aria-label={`Effectif idéal de ${role.name}`}
                      value={requirement.target}
                      disabled={busy}
                      onChange={(change) =>
                        update(role.id, { target: Number(change.target.value) })
                      }
                    />
                  )}
                </td>
                <td>
                  {requirement && (
                    <input
                      type="number"
                      min={0}
                      max={20}
                      style={{ width: 70 }}
                      aria-label={`Effectif maximal de ${role.name}`}
                      value={requirement.maximum}
                      disabled={busy}
                      onChange={(change) =>
                        update(role.id, { maximum: Number(change.target.value) })
                      }
                    />
                  )}
                </td>
                {candidates.length > 0 && (
                  <td>
                    {candidate && (
                      <span
                        className={`badge ${candidate.is_blocking ? 'required' : 'ok'}`}
                      >
                        {(candidate.eligible ?? []).length}
                      </span>
                    )}
                  </td>
                )}
                <td>{requirement && <PriorityBadge priority={requirement.priority} />}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Postes obligatoires qu'on ne saura pas pourvoir, avec le motif de chaque
 * exclusion. C'est l'explication du §15, montrée **avant** la génération.
 */
function BlockingList({ candidates, roles }: { candidates: Candidate[]; roles: Role[] }) {
  const blocking = candidates.filter((row) => row.is_blocking);
  const roleName = (roleId: number) =>
    roles.find((role) => role.id === roleId)?.name ?? `rôle ${roleId}`;

  if (blocking.length === 0) {
    return (
      <p className="muted" style={{ marginTop: 12 }}>
        Tous les postes obligatoires ont au moins un candidat disponible.
      </p>
    );
  }
  return (
    <div style={{ marginTop: 12 }}>
      <Banner kind="error">
        {blocking.length} poste{blocking.length > 1 ? 's' : ''} obligatoire
        {blocking.length > 1 ? 's' : ''} ne pourra pas être pourvu.
      </Banner>
      {blocking.map((row) => (
        <div key={row.role_id} className="card" style={{ marginBottom: 8 }}>
          <h3>{roleName(row.role_id)}</h3>
          <ul className="muted" style={{ margin: 0, paddingLeft: 18 }}>
            {(row.excluded ?? []).map((person) => (
              <li key={person.person_id}>
                <strong>{person.name}</strong> — {person.detail || person.reason}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}
